import {Link, useSearchParams} from 'react-router-dom';
import {useUser} from '../use-user';
import {ProgressCircle} from '@common/ui/progress/progress-circle';
import {SocialLoginPanel} from './social-login-panel';
import {BasicInfoPanel} from './basic-info-panel/basic-info-panel';
import {ChangePasswordPanel} from './change-password-panel/change-password-panel';
import {LocalizationPanel} from './localization-panel';
import {AccessTokenPanel} from './access-token-panel/access-token-panel';
import {DangerZonePanel} from './danger-zone-panel/danger-zone-panel';
import {Trans} from '@common/i18n/trans';
import {StaticPageTitle} from '@common/seo/static-page-title';
import {AccountSettingsPanel} from '@common/auth/ui/account-settings/account-settings-panel';
import {TwoFactorStepper} from '@common/auth/ui/two-factor/stepper/two-factor-auth-stepper';
import {AccountSettingsId} from '@common/auth/ui/account-settings/account-settings-sidenav';
import {SessionsPanel} from '@common/auth/ui/account-settings/sessions-panel/sessions-panel';
import {useContext, useEffect, useMemo, useState} from 'react';
import {SiteConfigContext} from '@common/core/settings/site-config-context';
import {SettingsIcon} from '@common/icons/material/Settings';
import {useAuth} from '@common/auth/use-auth';
import {useSettings} from '@common/core/settings/use-settings';
import {DashboardSidebar, DashboardBottomNav} from '@app/dashboard/dashboard-sidebar';
import {useLogout} from '@common/auth/requests/logout';
import {useIsDarkMode} from '@common/ui/themes/use-is-dark-mode';
import {useThemeSelector} from '@common/ui/themes/theme-selector-context';
import {useAccountUsage} from '@app/editor/use-account-usage';
import {openBuyAiUsageDialog} from '@app/editor/ai/usage/buy-ai-usage-dialog';
import {AiProviderSettingsPanel} from '@app/account/ai-provider-settings-panel';
import '@app/dashboard/dashboard-sidebar.css';
import './account-settings-page.css';

export function AccountSettingsPage() {
  const {auth} = useContext(SiteConfigContext);
  const [searchParams, setSearchParams] = useSearchParams();
  const panelFromUrl = searchParams.get('panel') || '';
  const {data, isLoading} = useUser('me', {
    with: ['roles', 'social_profiles', 'tokens'],
  });
  const [activeTab, setActiveTab] = useState<string>(panelFromUrl);
  const user = data?.user;

  useEffect(() => {
    if (panelFromUrl && panelFromUrl !== activeTab) {
      setActiveTab(panelFromUrl);
    }
  }, [activeTab, panelFromUrl]);

  const handleTabChange = (tabId: string) => {
    setActiveTab(tabId);
    if (!searchParams.has('panel') && tabId !== AccountSettingsId.AiUsage) {
      return;
    }
    const next = new URLSearchParams(searchParams);
    next.set('panel', tabId);
    setSearchParams(next, {replace: true});
  };

  return (
    <div className="acctShell">
      <StaticPageTitle>
        <Trans message="Account Settings" />
      </StaticPageTitle>

      {isLoading || !data ? (
        <div className="acctLoading">
          <ProgressCircle aria-label="Loading user.." isIndeterminate />
        </div>
      ) : (
        <AccountSettingsLayout
          activeTab={activeTab}
          onTabChange={handleTabChange}
          panels={auth.accountSettingsPanels ?? []}
          user={data.user}
        />
      )}
    </div>
  );
}

interface CustomPanel {
  id: string;
  label: {message: string} | any;
  icon?: React.ComponentType<any>;
  component: React.ComponentType<{user: any}>;
}

function AccountSettingsLayout({
  activeTab,
  onTabChange,
  panels,
  user,
}: {
  activeTab: string;
  onTabChange: (id: string) => void;
  panels: CustomPanel[];
  user: any;
}) {
  const {hasPermission} = useAuth();
  const {api, social, billing} = useSettings();
  const socialEnabled =
    social?.envato || social?.google || social?.facebook || social?.twitter;

  const tabs = useMemo(() => {
    const baseTabs = [
      {
        id: AccountSettingsId.AccountDetails,
        label: 'Profile',
        group: 'user' as const,
        icon: (
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
            <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/>
            <circle cx="12" cy="7" r="4"/>
          </svg>
        ),
        render: () => <BasicInfoPanel user={user} />,
      },
      {
        id: AccountSettingsId.LocationAndLanguage,
        label: 'Personalization',
        group: 'user' as const,
        icon: (
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
            <circle cx="12" cy="12" r="3"/>
            <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83 0 2 2 0 010-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 010-2.83 2 2 0 012.83 0l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 0 2 2 0 010 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z"/>
          </svg>
        ),
        render: () => <LocalizationPanel user={user} />,
      },
      ...(socialEnabled
        ? [{
            id: AccountSettingsId.SocialLogin,
            label: 'Connected accounts',
            group: 'user' as const,
            icon: (
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
                <path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71"/>
                <path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71"/>
              </svg>
            ),
            render: () => <SocialLoginPanel user={user} />,
          }]
        : []),
      {
        id: AccountSettingsId.Password,
        label: 'Password',
        group: 'user' as const,
        icon: (
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
            <rect x="3" y="11" width="18" height="11" rx="2"/>
            <path d="M7 11V7a5 5 0 0110 0v4"/>
          </svg>
        ),
        render: () => <ChangePasswordPanel />,
      },
      {
        id: AccountSettingsId.TwoFactor,
        label: 'Two-factor auth',
        group: 'user' as const,
        icon: (
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
            <rect x="5" y="2" width="14" height="20" rx="2"/>
            <line x1="12" y1="18" x2="12.01" y2="18"/>
          </svg>
        ),
        render: () => (
          <AccountSettingsPanel
            id={AccountSettingsId.TwoFactor}
            title={<Trans message="Two-factor authentication" />}
            description={<Trans message="Add an extra layer of security to your account using an authenticator app." />}
          >
            <div className="max-w-580">
              <TwoFactorStepper user={user} />
            </div>
          </AccountSettingsPanel>
        ),
      },
      {
        id: AccountSettingsId.Sessions,
        label: 'Active sessions',
        group: 'user' as const,
        icon: (
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
            <rect x="2" y="3" width="20" height="14" rx="2"/>
            <path d="M8 21h8M12 17v4"/>
          </svg>
        ),
        render: () => <SessionsPanel user={user} />,
      },
      {
        id: AccountSettingsId.AiUsage,
        label: 'AI usage',
        group: 'account' as const,
        icon: (
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 3L4 7v10l8 4 8-4V7z"/>
            <path d="M12 12l8-5"/>
            <path d="M12 12l-8-5"/>
            <path d="M12 12v9"/>
          </svg>
        ),
        render: () => <AiUsagePanel />,
      },
      {
        id: AccountSettingsId.AiProvider,
        label: 'AI provider',
        group: 'account' as const,
        icon: (
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 3l2.2 5.6L20 11l-5.8 2.4L12 19l-2.2-5.6L4 11l5.8-2.4z"/>
            <path d="M19 3v4M21 5h-4"/>
          </svg>
        ),
        render: () => <AiProviderSettingsPanel />,
      },
      ...(api?.integrated && hasPermission('api.access')
        ? [{
            id: AccountSettingsId.Developers,
            label: 'API tokens',
            group: 'account' as const,
            icon: (
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
                <polyline points="16 18 22 12 16 6"/>
                <polyline points="8 6 2 12 8 18"/>
              </svg>
            ),
            render: () => <AccessTokenPanel user={user} />,
          }]
        : []),
      {
        id: AccountSettingsId.DeleteAccount,
        label: 'Delete account',
        group: 'user' as const,
        icon: (
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
            <polyline points="3 6 5 6 21 6"/>
            <path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a1 1 0 011-1h4a1 1 0 011 1v2"/>
          </svg>
        ),
        render: () => <DangerZonePanel />,
      },
    ];

    const customTabs = panels.map(panel => ({
      id: panel.id,
      label: panel.label?.message ?? panel.id,
      group: 'user' as const,
      icon: panel.icon ? <panel.icon viewBox="0 0 50 50" /> : <SettingsIcon />,
      render: () => <panel.component user={user} />,
    }));

    return [...customTabs, ...baseTabs];
  }, [api?.integrated, hasPermission, panels, socialEnabled, user]);

  useEffect(() => {
    if (!tabs.length) return;
    if (!activeTab || !tabs.some(tab => tab.id === activeTab)) {
      onTabChange(tabs[0].id);
    }
  }, [activeTab, onTabChange, tabs]);

  const active = tabs.find(tab => tab.id === activeTab) ?? tabs[0];
  const userTabs = tabs.filter(t => t.group === 'user');
  const accountTabs = tabs.filter(t => t.group === 'account');

  const [mobilePanelId, setMobilePanelId] = useState<string | null>(null);
  const mobilePanelTab = tabs.find(t => t.id === mobilePanelId);

  return (
    <>
      {/* Mobile: Replit-style profile + menu (hidden on desktop via CSS) */}
      <MobileAccountMenu
        user={user}
        billing={billing}
        onMobilePanel={setMobilePanelId}
        className={mobilePanelId ? 'is-hidden' : ''}
      />

      {/* Mobile: panel drill-down view */}
      {mobilePanelId && (
        <div className="acctMobilePanelView">
          <div className="acctMobilePanelView__bar">
            <button
              type="button"
              className="acctMobilePanelView__back"
              onClick={() => setMobilePanelId(null)}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M19 12H5M12 5l-7 7 7 7"/>
              </svg>
              Back
            </button>
            {mobilePanelTab && (
              <span className="acctMobilePanelView__title">{mobilePanelTab.label}</span>
            )}
          </div>
          <div className="acctMobilePanelView__body">
            {mobilePanelTab?.render()}
          </div>
        </div>
      )}

      {/* Desktop: sidebar + content (hidden on mobile via CSS) */}
      <div className="acctLayout acctDesktopOnly">
        <DashboardSidebar
          activeItem="settings"
          navChildren={
            <nav className="db-sidebar__nav">
              {/* Back to dashboard */}
              <Link to="/dashboard" className="db-nav-item">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
                  <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/>
                  <polyline points="9 22 9 12 15 12 15 22"/>
                </svg>
                Dashboard
              </Link>

              {/* User tabs */}
              <span className="db-sidebar__group-label">User</span>
              {userTabs.map(tab => (
                <button
                  key={tab.id}
                  type="button"
                  className={`db-nav-item${tab.id === active?.id ? ' db-nav-item--active' : ''}${tab.id === AccountSettingsId.DeleteAccount ? ' db-nav-item--danger' : ''}`}
                  onClick={() => onTabChange(tab.id)}
                >
                  {tab.icon}
                  {tab.label}
                </button>
              ))}

              {/* Account tabs + billing */}
              {(accountTabs.length > 0 || billing?.enable) && (
                <>
                  <span className="db-sidebar__group-label">Account</span>
                  {billing?.enable && (
                    <Link to="/billing" className="db-nav-item">
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
                        <rect x="1" y="4" width="22" height="16" rx="2"/>
                        <line x1="1" y1="10" x2="23" y2="10"/>
                      </svg>
                      Billing
                    </Link>
                  )}
                  {accountTabs.map(tab => (
                    <button
                      key={tab.id}
                      type="button"
                      className={`db-nav-item${tab.id === active?.id ? ' db-nav-item--active' : ''}`}
                      onClick={() => onTabChange(tab.id)}
                    >
                      {tab.icon}
                      {tab.label}
                    </button>
                  ))}
                </>
              )}
            </nav>
          }
        />

        <main className="acctContent">
          <div className="acctContent__inner">
            {active?.render()}
          </div>
        </main>
      </div>

      {/* Bottom nav — fixed, CSS only shows on mobile */}
      <DashboardBottomNav activeTab="account" />
    </>
  );
}

function AiUsagePanel() {
  const {data: usage} = useAccountUsage();
  const text = usage?.ai.text;
  const images = usage?.ai.images;
  const topups = usage?.ai.topups;

  return (
    <AccountSettingsPanel
      id={AccountSettingsId.AiUsage}
      title={<Trans message="AI usage and limits" />}
      description={
        <Trans message="Track your current AI usage across text and image generation, including token and request windows." />
      }
    >
      <div className="space-y-14">
        <UsageBlock
          title="Text generation"
          hasPermission={!!text?.hasPermission}
          tokenUsed={text?.tokens?.used ?? text?.used ?? 0}
          tokenTotal={text?.tokens?.total ?? text?.total ?? null}
          dayUsed={text?.requests?.day?.used ?? 0}
          dayTotal={text?.requests?.day?.total ?? null}
          monthUsed={text?.requests?.month?.used ?? 0}
          monthTotal={text?.requests?.month?.total ?? null}
          failReason={text?.failReason}
          limitReason={text?.limitReason}
        />

        <UsageBlock
          title="Image generation"
          hasPermission={!!images?.hasPermission}
          tokenUsed={images?.tokens?.used ?? images?.used ?? 0}
          tokenTotal={images?.tokens?.total ?? images?.total ?? null}
          dayUsed={images?.requests?.day?.used ?? 0}
          dayTotal={images?.requests?.day?.total ?? null}
          monthUsed={images?.requests?.month?.used ?? 0}
          monthTotal={images?.requests?.month?.total ?? null}
          failReason={images?.failReason}
          limitReason={images?.limitReason}
        />

        <div className="rounded-lg border border-divider p-12">
          <div className="mb-6 text-sm font-semibold">Need more AI usage?</div>
          <div className="mb-10 text-xs text-muted">
            Buy extra monthly AI credits and apply them instantly to text and image limits.
          </div>
          <button
            type="button"
            className="inline-flex items-center justify-center rounded-md bg-primary px-12 py-8 text-xs font-semibold text-white hover:opacity-95 disabled:opacity-60"
            onClick={() =>
              openBuyAiUsageDialog({returnPath: '/account-settings?panel=ai-usage'})
            }
            disabled={topups?.enabled === false}
          >
            Buy extra usage
          </button>
          {topups?.active_bonus && (
            <div className="mt-10 text-xs text-muted">
              Active bonus: +{(topups.active_bonus.text?.tokens || 0).toLocaleString()} text tokens, +{(topups.active_bonus.images?.tokens || 0).toLocaleString()} image tokens.
            </div>
          )}
        </div>
      </div>
    </AccountSettingsPanel>
  );
}

function UsageBlock({
  title,
  hasPermission,
  tokenUsed,
  tokenTotal,
  dayUsed,
  dayTotal,
  monthUsed,
  monthTotal,
  failReason,
  limitReason,
}: {
  title: string;
  hasPermission: boolean;
  tokenUsed: number;
  tokenTotal: number | null;
  dayUsed: number;
  dayTotal: number | null;
  monthUsed: number;
  monthTotal: number | null;
  failReason?: string | null;
  limitReason?: string | null;
}) {
  return (
    <div className="rounded-lg border border-divider p-12">
      <div className="mb-10 flex items-center justify-between">
        <div className="text-sm font-semibold">{title}</div>
        <span className="text-xs text-muted">
          {hasPermission ? 'Enabled' : 'Not enabled for plan'}
        </span>
      </div>

      <UsageMeter
        label="Tokens (monthly)"
        used={tokenUsed}
        total={tokenTotal}
      />
      <UsageMeter
        label="Requests (daily)"
        used={dayUsed}
        total={dayTotal}
      />
      <UsageMeter
        label="Requests (monthly)"
        used={monthUsed}
        total={monthTotal}
      />

      {(failReason || limitReason) && (
        <div className="mt-8 text-xs text-danger">
          {usageFailureMessage(failReason, limitReason)}
        </div>
      )}
    </div>
  );
}

function UsageMeter({
  label,
  used,
  total,
}: {
  label: string;
  used: number;
  total: number | null;
}) {
  const percent = usagePercent(used, total);
  const usedLabel = used.toLocaleString();
  const totalLabel =
    total && total > 0 ? total.toLocaleString() : 'Unlimited';

  return (
    <div className="mb-8 last:mb-0">
      <div className="mb-4 flex items-center justify-between text-xs">
        <span className="text-muted">{label}</span>
        <span className="text-muted">
          {usedLabel} / {totalLabel}
        </span>
      </div>
      <div className="h-8 w-full overflow-hidden rounded-full bg-chip">
        <div
          className="h-full bg-primary"
          style={{width: `${percent}%`}}
        />
      </div>
    </div>
  );
}

function usagePercent(used: number, total: number | null): number {
  if (!total || total <= 0) return 0;
  return Math.max(0, Math.min(100, Math.round((used / total) * 100)));
}

function usageFailureMessage(
  failReason?: string | null,
  limitReason?: string | null,
): string {
  if (failReason === 'noPermission') {
    return 'This capability is not included in your plan.';
  }
  if (limitReason === 'overDailyLimit') {
    return 'Daily request limit reached.';
  }
  if (limitReason === 'overMonthlyLimit') {
    return 'Monthly request limit reached.';
  }
  if (limitReason === 'overTokenLimit') {
    return 'Token limit reached.';
  }
  return 'Usage limit reached.';
}

/* ─────────────────────────────────────────────────
   MOBILE ACCOUNT MENU
   ───────────────────────────────────────────────── */

function getInitials(user: any): string {
  const first = user?.first_name || '';
  const last  = user?.last_name  || '';
  if (first || last) return `${first[0] ?? ''}${last[0] ?? ''}`.toUpperCase();
  const name = user?.display_name || user?.name || user?.email || '';
  return name.slice(0, 2).toUpperCase();
}

interface MobileRowProps {
  icon: React.ReactNode;
  label: string;
  href?: string;
  external?: boolean;
  onClick?: () => void;
  danger?: boolean;
}

function MobileRow({icon, label, href, external, onClick, danger}: MobileRowProps) {
  const cls = `acctMobile__row${danger ? ' acctMobile__row--danger' : ''}`;

  const chevron = (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="9 18 15 12 9 6"/>
    </svg>
  );
  const externalIcon = (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6"/>
      <polyline points="15 3 21 3 21 9"/>
      <line x1="10" y1="14" x2="21" y2="3"/>
    </svg>
  );

  const inner = (
    <>
      <span className="acctMobile__row-icon">{icon}</span>
      <span className="acctMobile__row-label">{label}</span>
      <span className="acctMobile__row-right">{external ? externalIcon : (danger ? null : chevron)}</span>
    </>
  );

  if (onClick) {
    return <button type="button" className={cls} onClick={onClick}>{inner}</button>;
  }
  if (external && href) {
    return <a href={href} target="_blank" rel="noreferrer" className={cls}>{inner}</a>;
  }
  return <Link to={href ?? '/account-settings'} className={cls}>{inner}</Link>;
}

function MobileAccountMenu({
  user,
  billing,
  onMobilePanel,
  className,
}: {
  user: any;
  billing: any;
  onMobilePanel: (id: string) => void;
  className?: string;
}) {
  const isDark = useIsDarkMode();
  const logout = useLogout();
  const {selectedTheme, selectTheme} = useThemeSelector();
  const canChangeTheme = !!selectedTheme;

  const displayName = user?.display_name
    || (`${user?.first_name ?? ''} ${user?.last_name ?? ''}`.trim())
    || user?.email
    || 'User';
  const initials   = getInitials(user);
  const username   = user?.username || null;
  const bio        = (user as any)?.profile?.description || (user as any)?.bio || null;
  const subscription = user?.subscriptions?.find((s: any) => s.valid);
  const planName   = (subscription as any)?.product?.name || null;

  return (
    <div className={`acctMobileMenu${className ? ` ${className}` : ''}`}>
      {/* ── Hero ── */}
      <div className="acctMobile__hero">
        <div className="acctMobile__avatar">{initials}</div>

        <div className="acctMobile__name-row">
          <span className="acctMobile__name">{displayName}</span>
          {planName && (
            <span className="acctMobile__plan-badge">✦ {planName}</span>
          )}
        </div>

        {username && (
          <div className="acctMobile__meta-row">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
              <circle cx="12" cy="12" r="4"/>
              <path d="M16 8v5a3 3 0 006 0v-1a10 10 0 10-3.92 7.94"/>
            </svg>
            {username}
          </div>
        )}

        {user?.email && (
          <div className="acctMobile__meta-row">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
              <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
              <polyline points="22,6 12,13 2,6"/>
            </svg>
            {user.email}
          </div>
        )}

        <p className={`acctMobile__bio${!bio ? ' acctMobile__bio--empty' : ''}`}>
          {bio ?? "You don't have a bio yet..."}
        </p>
      </div>

      <div className="acctMobile__divider" />

      {/* ── Menu ── */}
      <div className="acctMobile__menu">

        {/* Billing + Usage (no label) — only if billing enabled */}
        {billing?.enable && (
          <div className="acctMobile__group">
            <MobileRow
              href="/billing"
              label="Billing"
              icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><rect x="1" y="4" width="22" height="16" rx="2"/><line x1="1" y1="10" x2="23" y2="10"/></svg>}
            />
            <MobileRow
              href="/billing"
              label="Usage"
              icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></svg>}
            />
          </div>
        )}

        <div className="acctMobile__group">
          <span className="acctMobile__group-label">Account</span>
          <MobileRow
            onClick={() => onMobilePanel(AccountSettingsId.AiUsage)}
            label="AI usage"
            icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3L4 7v10l8 4 8-4V7z"/><path d="M12 12l8-5"/><path d="M12 12l-8-5"/><path d="M12 12v9"/></svg>}
          />
          <MobileRow
            onClick={() => onMobilePanel(AccountSettingsId.AiProvider)}
            label="AI provider"
            icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3l2.2 5.6L20 11l-5.8 2.4L12 19l-2.2-5.6L4 11l5.8-2.4z"/><path d="M19 3v4M21 5h-4"/></svg>}
          />
        </div>

        {/* PROFILE */}
        <div className="acctMobile__group">
          <span className="acctMobile__group-label">Profile</span>
          <MobileRow
            onClick={() => onMobilePanel(AccountSettingsId.AccountDetails)}
            label="Edit Profile"
            icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>}
          />
          <MobileRow
            href="/referrals"
            label="Refer and earn credits"
            icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><polyline points="20 12 20 22 4 22 4 12"/><rect x="2" y="7" width="20" height="5"/><line x1="12" y1="22" x2="12" y2="7"/><path d="M12 7H7.5a2.5 2.5 0 010-5C11 2 12 7 12 7z"/><path d="M12 7h4.5a2.5 2.5 0 000-5C13 2 12 7 12 7z"/></svg>}
          />
        </div>

        {/* THEME */}
        <div className="acctMobile__group">
          <span className="acctMobile__group-label">Theme</span>
          <MobileRow
            onClick={canChangeTheme ? () => selectTheme(isDark ? 'light' : 'dark') : undefined}
            href={canChangeTheme ? undefined : '/account-settings'}
            label={`Theme — ${isDark ? 'Dark' : 'Light'}`}
            icon={isDark
              ? <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><path d="M21 12.79A9 9 0 1111.21 3 7 7 0 0021 12.79z"/></svg>
              : <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><circle cx="12" cy="12" r="5"/><line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/><line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/><line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/><line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/></svg>
            }
          />
        </div>

        {/* NOTIFICATIONS */}
        <div className="acctMobile__group">
          <span className="acctMobile__group-label">Notifications</span>
          <MobileRow
            href="/notifications"
            label="Notifications"
            icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 01-3.46 0"/></svg>}
          />
        </div>

        {/* SUPPORT */}
        <div className="acctMobile__group">
          <span className="acctMobile__group-label">Support</span>
          <MobileRow
            href="/help"
            label="Help"
            icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 015.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>}
          />
          <MobileRow
            href="https://docs.piksera.com"
            label="Docs"
            external
            icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><path d="M2 3h6a4 4 0 014 4v14a3 3 0 00-3-3H2z"/><path d="M22 3h-6a4 4 0 00-4 4v14a3 3 0 013-3h7z"/></svg>}
          />
        </div>

        {/* OTHER */}
        <div className="acctMobile__group">
          <span className="acctMobile__group-label">Other</span>
          <MobileRow
            href="/about"
            label="About"
            icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>}
          />
          <MobileRow
            onClick={() => onMobilePanel(AccountSettingsId.Password)}
            label="Manage Account"
            icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>}
          />
          <MobileRow
            label="Log Out"
            danger
            onClick={() => logout.mutate()}
            icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>}
          />
        </div>

      </div>
    </div>
  );
}
